import * as React from 'react';
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {none, Option, option} from "ts-option";
import {OnFailure, OnSuccess} from "../../store/action/actionHelpers";
import * as validator from "validator";
import Toast from "../helper/Toast";
import {requestResetPassword} from "../../store/action/user/user";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import "./forgotPasswordPage.scss";
import Input from "../../hiberUiKit/src/atoms/Input/Input";
import {isEmail} from "../../utils/ValidationUtils";
import DrivingGreenLogo from "./DrivingGreenLogo";

const successToastHeader = "Password reset";

const successToastContent = "If your email is in our database, you will soon receive a message with instructions on how to reset your password.";

interface DispatchProps {
	/**
	 * Request reset password action.
	 * @param {string} email user email
	 * @param {OnSuccess} onSuccess Success callback.
	 * @param {OnFailure} onFailure Failure callback.
	 */
	requestResetPassword: (email: string, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

interface State {
	/**
	 * User email.
	 */
	email: Option<string>,
	/**
	 * Indicates if request is in progress.
	 */
	loading: boolean,
	/**
	 * Error from sending email address attempt.
	 */
	error: Option<string>
}

type Props = DispatchProps;

class ForgotPasswordForm extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {email: none, loading: false, error: none};
		this.updateEmail = this.updateEmail.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.onSuccess = this.onSuccess.bind(this);
		this.onFailed = this.onFailed.bind(this);
	}

	render() {
		return (
			<div className={"c-forgot-password"}>
				<div className={"c-forgot-password_content"}>
					<DrivingGreenLogo className="c-forgot-password_content_logo"/>
					<h1 className="c-forgot-password_content_header">Reset password</h1>
					<p className="c-forgot-password_content_intro">Enter your email address and we’ll send you a link to reset your password.</p>
					<div className="c-forgot-password_content_email">
						<label htmlFor="reset-email">Email</label>
						<Input id="reset-email"
							   type={"text"}
							   value={this.state.email.getOrElse("")}
							   onChange={this.updateEmail}
							   placeholder={"Enter your email address"}
							   focus={true}
							   onConfirm={this.onSubmit}/>
					</div>
					<div className={"c-forgot-password_content_error"} role="alert" aria-live="polite">
						{this.renderError()}
					</div>
					<div className={"c-forgot-password_content_button"}>
						<ButtonPrimary label={"Send email"} variant={"success"} onClick={this.onSubmit} isLoading={this.state.loading}/>
					</div>
					<div className={"c-forgot-password_content_link"}>
						<a href={"/"}>Take me back to Sign In</a>
					</div>
				</div>
			</div>
		);
	}

	/**
	 * Render submit error.
	 */
	renderError() {
		return this.state.error.map((error) => <span>{error}</span>).orNull
	}

	/**
	 * Update email.
	 */
	updateEmail(evt: React.ChangeEvent<HTMLInputElement>) {
		this.setState({email: option(evt.target.value), error: none});
	};

	/**
	 * On submitting check email and if its valid send password reset request.
	 */
	private onSubmit() {
		const error = this.validateInputs();
		this.setState({loading: error.isEmpty, error});
		if (error.isEmpty) {
			this.props.requestResetPassword(this.state.email.getOrElse(""), this.onSuccess, this.onFailed);
		}
	}

	/**
	 * Validate inputs.
	 * @returns {Option<string>} Optional error containing error string to display.
	 */
	private validateInputs(): Option<string> {
		const givenEmail = this.state.email.getOrElse("");
		if (validator.isEmpty(givenEmail))
			return option("Please provide your email address");
		else if (!isEmail(givenEmail))
			return option("Provided email address is not valid");
		else
			return none;
	}

	/**
	 * On success email submitted.
	 */
	private onSuccess() {
		this.setState({loading: false, error: none});
		Toast.success(successToastContent, successToastHeader);
	}

	/**
	 * On failed.
	 * @param error
	 */
	private onFailed(error: any) {
		const regex = new RegExp(/^5[0-9]{2}$/);
		this.setState({loading: false});
		if (error && error.response && regex.test(error.response.status)) {
			this.setState({error: option("Something went wrong. Please try again later.")});
		}
		else {
			Toast.success(successToastContent, successToastHeader);
		}
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		requestResetPassword: (email: string, onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(requestResetPassword(email, onSuccess, onFailure))
	}
}

export default connect(null, mapDispatchToProps)(ForgotPasswordForm)
